"use client";

import { useState } from "react";
import { Pause, Play } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import styles from "./arena.module.css";

export type TickerToolchain = { name: string; detail: string };

/**
 * The judge's languages on their real toolchains, read from the runner's own
 * catalogue (`EX-PUB19`). A 64-second lap that pauses on hover, on focus and
 * with its button, because anything that moves for longer than five seconds
 * needs a way to stop it (`docs/03` §7). Under reduced motion it is a still,
 * wrapped row.
 */
export function ToolchainTicker({ toolchains }: { toolchains: TickerToolchain[] }) {
  const t = useTranslations("judge.home");
  const [paused, setPaused] = useState(false);
  if (toolchains.length === 0) return null;

  const names = toolchains.map((toolchain) => toolchain.name).join(", ");

  return (
    <div className="flex flex-col items-start gap-3 py-5 sm:h-[76px] sm:flex-row sm:items-center sm:gap-6 sm:py-0">
      <span className="shrink-0 whitespace-nowrap text-xs font-bold uppercase tracking-[0.14em] text-neutral-400">
        {t("tickerLabel", { count: toolchains.length })}
      </span>
      <div className={cn(styles.ticker, paused && styles.paused, "w-full min-w-0 flex-1")} role="img" aria-label={names}>
        <div className={styles.track} aria-hidden>
          {(["first", "second"] as const).flatMap((copy) =>
            toolchains.map((toolchain) => (
              <span
                key={`${copy}-${toolchain.name}`}
                data-copy={copy}
                className={cn(
                  styles.tickerItem,
                  "inline-flex h-9 items-center gap-2.5 whitespace-nowrap rounded-full border border-white/10 bg-neutral-900 px-3.5",
                )}
              >
                <span className="text-[13.5px] font-medium text-white">{toolchain.name}</span>
                <span className={cn(styles.mono, "text-xs text-neutral-400")}>{toolchain.detail}</span>
              </span>
            )),
          )}
        </div>
      </div>
      <Button
        type="button"
        variant="ghostDark"
        onClick={() => setPaused((value) => !value)}
        aria-label={paused ? t("tickerPlay") : t("tickerPause")}
        aria-pressed={paused}
        className="hidden h-8 w-8 shrink-0 rounded-full border border-white/15 p-0 motion-reduce:hidden sm:inline-flex"
      >
        {paused ? <Play className="h-3.5 w-3.5" aria-hidden /> : <Pause className="h-3.5 w-3.5" aria-hidden />}
      </Button>
    </div>
  );
}
